import { useContext, useMemo } from "react";
import { useTextDomainContext } from "@/app/providers";
import { string } from "prop-types";

type UsePriceTypesProps = {
  countryCode?: string;
};

export const usePriceTypes = (props: UsePriceTypesProps = {}) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const propsDefault = {
    countryCode: "DK",
  };

  // Use props directly with fallback instead of state
  const countryCode = props.countryCode || propsDefault.countryCode;

  // Calculate and return priceTypes using useMemo to optimize performance
  return useMemo(() => {
    const priceTypes1 = [
      {
        value: "fixed-price",
        label: gettext("Fixed price"),
        price: true,
        system: false,
        epad: false,
        profile: false,
        markup: false,
        commission: true,
      },
      {
        value: "fixed-price-ex-profile",
        label: gettext("Fixed price ex profile"),
        price: false,
        system: true,
        epad: true,
        profile: false,
        markup: false,
        commission: true,
      },
      {
        value: "fixed-price-ex-epad",
        label: gettext("Fixed price ex epad"),
        price: false,
        system: true,
        epad: false,
        profile: false,
        markup: true,
        commission: true,
      },
      {
        value: "fixed-price-ex-epad-and-profile",
        label: gettext("Fixed price ex epad and profile"),
        price: false,
        system: true,
        epad: false,
        profile: false,
        markup: false,
        commission: true,
      },
      {
        value: "variable-spot-nordpool-price",
        label: gettext("Variable/Spot/Nordpool price"),
        price: false,
        system: false,
        epad: false,
        profile: false,
        markup: false,
        commission: true,
      },
    ];

    const priceTypes2 = [
      {
        value: "fixed-price",
        label: gettext("Fixed price"),
        price: true,
        system: false,
        epad: false,
        profile: false,
        markup: false,
        commission: true,
      },
      {
        value: "fixed-price-ex-profile",
        label: gettext("Fixed price ex profile"),
        price: false,
        system: true,
        epad: true,
        profile: false,
        markup: false,
        commission: true,
      },
      {
        value: "fixed-profile-ex-spot",
        label: gettext("Fixed profile ex spot"),
        price: false,
        system: true,
        epad: false,
        profile: false,
        markup: true,
        commission: true,
      },
      {
        value: "variable-spot-nordpool-price",
        label: gettext("Variable/Spot/Nordpool price"),
        price: false,
        system: false,
        epad: false,
        profile: false,
        markup: false,
        commission: true,
      },
    ];

    switch (countryCode) {
      case "DE":
        return priceTypes2;
      case "all": {
        const allTypes = [...priceTypes1];
        priceTypes2.forEach((pt) => {
          if (!allTypes.some((t) => t.value === pt.value)) {
            allTypes.push(pt);
          }
        });
        return allTypes;
      }
      default:
        return priceTypes1;
    }
  }, [countryCode, gettext]);
};

usePriceTypes.propTypes = {
  countryCode: string,
};

// Commission =>Sport Premium
